import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { LegalLinks } from "@/features/legal/LegalLinks";

/** What the person ticks: the Terms (with the DPA, an annex to them) and
 *  that they have read the Privacy Policy (Terms 2.3). Both are needed. */
type Ticks = { terms: boolean; privacy: boolean };

/** The terms step, shared by the first-sign-in page and the gate an
 *  existing user meets after a new version: links to read the documents
 *  (in a new tab, so the step keeps its place), the two boxes, Accept and
 *  one way out (Cancel for a new sign-in, Sign out for an account). */
export function TermsAcceptanceForm({
  intro,
  secondaryLabel,
  onAccept,
  onSecondary,
  busy,
  error,
  optional,
}: {
  intro: React.ReactNode;
  /** Optional boxes under the two required ones (the sign-up's product
   *  news); Accept never depends on them. */
  optional?: React.ReactNode;
  secondaryLabel: string;
  onAccept: () => void;
  onSecondary: () => void;
  busy: boolean;
  error: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [ticks, setTicks] = React.useState<Ticks>({ terms: false, privacy: false });
  const ready = ticks.terms && ticks.privacy;

  return (
    <Stack
      component="form"
      sx={{ gap: 2.5 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) onAccept();
      }}
    >
      <Box>{intro}</Box>
      <Typography variant="body2" color="text.secondary">
        {t("legal.accept.read_first")}
      </Typography>
      <LegalLinks docs={["terms", "privacy", "dpa"]} newTab />
      <Stack sx={{ gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={ticks.terms}
              onChange={(event) => setTicks((prev) => ({ ...prev, terms: event.target.checked }))}
            />
          }
          label={t("legal.accept.terms_checkbox")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={ticks.privacy}
              onChange={(event) =>
                setTicks((prev) => ({ ...prev, privacy: event.target.checked }))
              }
            />
          }
          label={t("legal.accept.privacy_checkbox")}
        />
        {optional}
      </Stack>
      {error && (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      )}
      <Stack direction="row" sx={{ gap: 1.5, justifyContent: "flex-end" }}>
        <Button variant="text" color="inherit" onClick={onSecondary} disabled={busy}>
          {secondaryLabel}
        </Button>
        <Button type="submit" variant="contained" disabled={!ready || busy}>
          {busy ? t("legal.accept.accepting") : t("legal.accept.accept")}
        </Button>
      </Stack>
    </Stack>
  );
}
